"use client";

import { useState, useTransition, type FormEvent } from "react";
import { cn } from "@/lib/utils";

type Step = "url" | "contact" | "success";

function collectUtm(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }
  const out: Record<string, string> = {};
  new URLSearchParams(window.location.search).forEach((v, k) => {
    if (k.startsWith("utm_")) {
      out[k] = v;
    }
  });
  return out;
}

export function LeadForm({
  placement,
  onUrlSubmit,
}: {
  placement: "hero" | "final";
  onUrlSubmit?: (domain: string) => void;
}) {
  const [step, setStep] = useState<Step>("url");
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  function submitUrl(e: FormEvent) {
    e.preventDefault();
    const d = domain.trim();
    if (d.length < 3) {
      return;
    }
    onUrlSubmit?.(d);
    setStep("contact");
  }

  function submitContact(e: FormEvent) {
    e.preventDefault();
    setError(null);
    startTransition(async () => {
      try {
        const res = await fetch("/api/marketing/lead", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            domain: domain.trim(),
            name,
            email,
            phone,
            company,
            utm: collectUtm(),
          }),
        });
        const data = await res.json();
        if (data?.ok) {
          setStep("success");
        } else {
          setError("Something went wrong — please try again.");
        }
      } catch {
        setError("Network error — please try again.");
      }
    });
  }

  const input =
    "w-full rounded-(--radius-sm) border border-(--color-border-strong) bg-(--color-surface-1) px-4 py-3 text-sm outline-none focus:border-(--color-brand-accent)";
  const data = placement === "hero" ? "hero" : "final";

  if (step === "success") {
    return (
      <div
        data-placement={data}
        className="rounded-(--radius-md) border border-(--color-border) bg-(--color-surface-1) p-6 text-sm"
      >
        <p className="font-medium">Thanks — we&apos;ll be in touch with your redesign.</p>
      </div>
    );
  }

  if (step === "url") {
    return (
      <form data-placement={data} onSubmit={submitUrl} className="flex flex-col gap-3 sm:flex-row">
        <label className="sr-only" htmlFor={`mkt-url-${data}`}>
          Your website
        </label>
        <input
          id={`mkt-url-${data}`}
          name="website"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="yourdealership.com"
          className={input}
          autoComplete="url"
        />
        <button
          type="submit"
          className="rounded-(--radius-sm) bg-(--color-brand-accent) px-5 py-3 text-sm font-medium text-(--color-on-accent) transition-all hover:shadow-(--shadow-glow)"
        >
          Show my redesign →
        </button>
      </form>
    );
  }

  return (
    <form data-placement={data} onSubmit={submitContact} className="flex flex-col gap-3">
      <p className="text-sm text-(--color-foreground-muted)">Where should we send your redesign?</p>
      <label className="sr-only" htmlFor={`mkt-name-${data}`}>
        Name
      </label>
      <input
        id={`mkt-name-${data}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        className={input}
        autoComplete="name"
        required
      />
      <label className="sr-only" htmlFor={`mkt-email-${data}`}>
        Email
      </label>
      <input
        id={`mkt-email-${data}`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        className={input}
        autoComplete="email"
        required
      />
      <label className="sr-only" htmlFor={`mkt-phone-${data}`}>
        Phone
      </label>
      <input
        id={`mkt-phone-${data}`}
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Phone"
        className={input}
        autoComplete="tel"
        required
      />
      {/* Honeypot — visually hidden, off-screen, not announced. */}
      <input
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        className="absolute -left-[9999px] h-0 w-0"
        name="company"
      />
      {error ? (
        <p className="text-sm text-(--color-destructive)" role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={pending}
        className={cn(
          "rounded-(--radius-sm) bg-(--color-brand-accent) px-5 py-3 text-sm font-medium text-(--color-on-accent) disabled:opacity-60",
        )}
      >
        {pending ? "Sending…" : "Send my redesign →"}
      </button>
    </form>
  );
}
